import React from 'react';
import { useSelector } from 'react-redux';
import { notification } from 'antd';
import { OpTable, IOpTableProps } from 'components/customAntd/DLS/OpTable/OpTable';
import { OpTableRawColumnType } from 'components/customAntd/DLS/OpTableCore/OpTableCore';
import { RootState } from 'store/store';
import { TABLE_HEIGHT } from 'constants/ui';
import { Agreement } from 'types/agreementTypes';
import { hasPermission } from 'utils/utils';
import { deleteRequest, getRequest, postRequest } from 'api/apiClient';
import { AGREEMENT_DELETED_ID } from 'constants/userActivities';
import { useTranslation } from 'react-i18next';

interface AgreementsTableProps {
    agreements: Agreement[];
    loading: boolean;
    setAgreements: (agreement: Agreement[]) => void;
    handleEditClick: (agreement: Agreement) => void;
    handleAddClick: () => void;
}

const AgreementsTable: React.FC<AgreementsTableProps> = ({ agreements, loading, setAgreements, handleEditClick, handleAddClick }) => {
    const orgId = useSelector((state: RootState) => state.globalOrg.globalOrgId);
    const globalUserId = useSelector((state: RootState) => state.users.globalUser?.id);
    const tokenScopeList = useSelector((state: RootState) => state.auth.auth.data[0]?.tokenScopeList || []);
    const hasAgreementsWrite = hasPermission(tokenScopeList, orgId, 'o', 'agreements:w');
    const { t } = useTranslation();

    const handleDeleteAgreement = async (agreement: Agreement) => {
        try {
            await deleteRequest(`/orgs/${orgId}/termsAgreements/${agreement.id}`);
            await postRequest(`/orgs/${orgId}/userActivity`, {
                userId: globalUserId,
                activityId: AGREEMENT_DELETED_ID,
                details: agreement.name
            });

            // Re-fetch the updated list of agreements
            const agreements = await getRequest(`/orgs/${orgId}/termsAgreements`);
            const filteredAgreements = agreements.data.filter((agreement: Agreement) => agreement.status === 1);
            setAgreements(filteredAgreements);

            notification.success({
                message: t('notification.success'),
                description: t('agreement.notification.deleteSuccess'),
                placement: 'bottomRight',
            });
        } catch (error) {
            notification.error({
                message: t('notification.error'),
                description: t('agreement.notification.deleteError'),
                placement: 'bottomRight',
            });
        }
    };

    const columns: OpTableRawColumnType[] = [
        {
            dataIndex: 'name',
            label: t('agreement.table.name'),
            filter: {
                type: 'input',
            },
            sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
        },
    ];

    const opTableProps: IOpTableProps = {
        dataSource: agreements,
        columns: columns,
        rowActions: {
            onEditClick: (agreement: Agreement) => { handleEditClick(agreement) },
            onDeleteClick: hasAgreementsWrite ? handleDeleteAgreement : undefined,
            deleteModalTitle: () => t('confirmDelete.title', { title: t('agreement.itemName') }),
            deleteModalContent: ({ record }) => t('confirmDelete.message', { name: record.name }),
        },
        height: TABLE_HEIGHT,
        allowGlobalSearch: true,
        writeAccess: hasAgreementsWrite,
        allowAddition: hasAgreementsWrite ? {
            itemName: t('agreement.itemName'),
            onClick: () => handleAddClick(),
        } : false,
        loading: loading,
        allowExport: true,
        allowShowHideColumns: true,
    };

    return <OpTable {...opTableProps} />;
};

export default AgreementsTable;
